import { useCallback, useEffect, useRef, useState } from "react";
import { attachCallBackgroundGuard } from "./call-background.js";
import { applySpeakerRoute } from "./callAudioRoute.js";

/**
 * useCall — owns the full 1:1 voice-call lifecycle for the widget.
 *
 * Signaling rides the existing authenticated socket (call:* events, see
 * useSocket.js); media is a peer-to-peer RTCPeerConnection with STUN/TURN
 * from `call:ice-config`. The visitor can be caller (startCall) or callee
 * (call:incoming → acceptCall/rejectCall).
 *
 * The remote audio element lives inside the widget's Shadow DOM — the host
 * component renders `<audio autoPlay playsInline ref={attachAudioElement}>`
 * and this hook attaches the remote stream to it. `.play()` is retried from
 * the accept/start click handlers to satisfy autoplay policy.
 *
 * @param {object} socketApi - Return value of useSocket()
 */

const RING_TIMEOUT_MS = 35_000; // caller gives up ringing
const INCOMING_DISMISS_MS = 45_000; // callee safety auto-dismiss (server TTL backstop)
const ENDED_DISPLAY_MS = 2_500; // transient "No answer"/"Busy"/… before idle

const IDLE_STATE = {
    // Loudspeaker vs earpiece. Defaults ON because mobile browsers already play
    // WebRTC audio through the speaker; the first tap therefore goes quiet.
    speakerOn: true,
    status: "idle", // idle | outgoing | incoming | connecting | connected | ended
    callId: null,
    conversationId: null,
    peer: null, // { name, avatar } — shop/staff info for the call UI
    muted: false,
    durationSec: 0,
    endedReason: null,
};

/** Statuses where a call is live — ringing out, negotiating, or talking.
 *  "incoming" is excluded: IncomingCallPanel already takes over the screen. */
export const isCallActive = (status) =>
    status === "outgoing" || status === "connecting" || status === "connected";

export const formatCallDuration = (totalSec) => {
    const minutes = Math.floor((totalSec || 0) / 60);
    const seconds = (totalSec || 0) % 60;
    return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

const createCallId = () => {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return `call-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const micErrorReason = (err) =>
    err?.name === "NotAllowedError" || err?.name === "SecurityError"
        ? "microphone-blocked"
        : "microphone-missing";

export function useCall(socketApi) {
    const {
        emitCallOffer,
        emitCallAnswer,
        emitCallIceCandidate,
        emitCallReject,
        emitCallHangup,
        emitCallCancel,
        requestIceConfig,
        onCallIncoming,
        onCallAnswered,
        onCallIceCandidate,
        onCallRejected,
        onCallCancelled,
        onCallEnded,
        onCallBusy,
        onCallUnavailable,
        onCallError,
    } = socketApi;

    const [callState, setCallState] = useState(IDLE_STATE);

    // ── Refs (socket handlers read these — never stale closures) ─────
    const callRef = useRef(null); // { callId, conversationId, role: "caller"|"callee" }
    const pcRef = useRef(null);
    const localStreamRef = useRef(null);
    const backgroundGuardRef = useRef(null);
    const audioElRef = useRef(null);
    const remoteStreamRef = useRef(null); // remote MediaStream, re-attached if the <audio> node swaps
    const incomingOfferRef = useRef(null); // full call:incoming payload
    const pendingCandidatesRef = useRef([]); // remote ICE before remoteDescription
    const seenIncomingRef = useRef(new Set()); // de-dupe call:incoming by callId
    const ringTimerRef = useRef(null);
    const incomingTimerRef = useRef(null);
    const durationTimerRef = useRef(null);
    const endedTimerRef = useRef(null);
    const connectedAtRef = useRef(null);
    const iceGraceTimerRef = useRef(null); // grace window for transient ICE disconnects

    const attachAudioElement = useCallback((node) => {
        audioElRef.current = node;
        // If the <audio> node remounts mid-call (e.g. a page/route change),
        // re-bind the live remote stream so audio doesn't cut.
        if (node && remoteStreamRef.current) {
            node.srcObject = remoteStreamRef.current;
            node.play?.().catch(() => {});
        }
    }, []);

    // Best-effort `.play()` — called from click gestures to unlock autoplay
    // and again from ontrack once the remote stream exists.
    const playRemoteAudio = useCallback(() => {
        const el = audioElRef.current;
        if (!el) return;
        const promise = el.play();
        if (promise !== undefined) {
            promise.catch(() => {
                // No source yet or autoplay blocked — ontrack retries.
            });
        }
    }, []);

    // ── Teardown (idempotent) ─────────────────────────────────────────
    const teardown = useCallback((endedReason = null) => {
        if (ringTimerRef.current) {
            clearTimeout(ringTimerRef.current);
            ringTimerRef.current = null;
        }
        if (incomingTimerRef.current) {
            clearTimeout(incomingTimerRef.current);
            incomingTimerRef.current = null;
        }
        if (durationTimerRef.current) {
            clearInterval(durationTimerRef.current);
            durationTimerRef.current = null;
        }
        if (endedTimerRef.current) {
            clearTimeout(endedTimerRef.current);
            endedTimerRef.current = null;
        }
        if (iceGraceTimerRef.current) {
            clearTimeout(iceGraceTimerRef.current);
            iceGraceTimerRef.current = null;
        }

        if (localStreamRef.current) {
            localStreamRef.current.getTracks().forEach((track) => {
                try {
                    track.stop();
                } catch {}
            });
            localStreamRef.current = null;
        }

        const pc = pcRef.current;
        if (pc) {
            pc.onicecandidate = null;
            pc.ontrack = null;
            pc.onconnectionstatechange = null;
            try {
                pc.close();
            } catch {}
            pcRef.current = null;
        }

        remoteStreamRef.current = null;
        if (audioElRef.current) {
            audioElRef.current.srcObject = null;
        }

        if (backgroundGuardRef.current) {
            backgroundGuardRef.current();
            backgroundGuardRef.current = null;
        }

        pendingCandidatesRef.current = [];
        incomingOfferRef.current = null;
        callRef.current = null;
        connectedAtRef.current = null;

        if (endedReason) {
            // Transient terminal state so the UI can show "No answer"/"Busy"…
            setCallState({ ...IDLE_STATE, status: "ended", endedReason });
            endedTimerRef.current = setTimeout(() => {
                endedTimerRef.current = null;
                setCallState(IDLE_STATE);
            }, ENDED_DISPLAY_MS);
        } else {
            setCallState(IDLE_STATE);
        }
    }, []);

    // ── Shared RTCPeerConnection setup ────────────────────────────────
    const createPeerConnection = useCallback(
        (iceServers, callId, conversationId) => {
            const pc = new RTCPeerConnection({ iceServers });
            pcRef.current = pc;

            pc.onicecandidate = (event) => {
                if (!event.candidate) return;
                if (callRef.current?.callId !== callId) return;
                emitCallIceCandidate({
                    callId,
                    conversationId,
                    candidate: event.candidate,
                });
            };

            pc.ontrack = (event) => {
                const stream =
                    event.streams?.[0] || new MediaStream([event.track]);
                remoteStreamRef.current = stream;
                const el = audioElRef.current;
                if (!el) return;
                el.srcObject = stream;
                playRemoteAudio();
            };

            pc.onconnectionstatechange = () => {
                if (callRef.current?.callId !== callId) return;
                const state = pc.connectionState;
                if (state === "connected") {
                    if (!connectedAtRef.current) {
                        connectedAtRef.current = Date.now();
                        durationTimerRef.current = setInterval(() => {
                            setCallState((prev) =>
                                prev.status === "connected"
                                    ? {
                                          ...prev,
                                          durationSec: Math.floor(
                                              (Date.now() -
                                                  connectedAtRef.current) /
                                                  1000,
                                          ),
                                      }
                                    : prev,
                            );
                        }, 1000);
                    }
                    if (iceGraceTimerRef.current) {
                        clearTimeout(iceGraceTimerRef.current);
                        iceGraceTimerRef.current = null;
                    }
                    setCallState((prev) => ({ ...prev, status: "connected" }));
                } else if (state === "failed") {
                    emitCallHangup({ callId, conversationId, reason: state });
                    teardown("connection-lost");
                } else if (state === "disconnected") {
                    // Transient (network handoff) and usually recovers — give
                    // it a grace window before dropping the call.
                    if (!iceGraceTimerRef.current) {
                        iceGraceTimerRef.current = setTimeout(() => {
                            iceGraceTimerRef.current = null;
                            if (callRef.current?.callId !== callId) return;
                            if (pc.connectionState === "connected") return;
                            emitCallHangup({ callId, conversationId, reason: "disconnected" });
                            teardown("connection-lost");
                        }, 8000);
                    }
                }
            };

            return pc;
        },
        [emitCallIceCandidate, emitCallHangup, playRemoteAudio, teardown],
    );

    const flushPendingCandidates = useCallback(() => {
        const pc = pcRef.current;
        if (!pc || !pc.remoteDescription) return;
        const queued = pendingCandidatesRef.current;
        pendingCandidatesRef.current = [];
        queued.forEach((candidate) => {
            pc.addIceCandidate(candidate).catch((err) => {
                console.warn("[SalesiveCall] addIceCandidate failed", err);
            });
        });
    }, []);

    const getMicStream = useCallback(async () => {
        if (!navigator.mediaDevices?.getUserMedia) {
            const err = new Error("Microphone not supported");
            err.name = "NotFoundError";
            throw err;
        }
        return navigator.mediaDevices.getUserMedia({ audio: true });
    }, []);

    // ── Caller: start an outgoing call ────────────────────────────────
    const startCall = useCallback(
        async (conversationId, peer = null) => {
            if (!conversationId || callRef.current) return;
            if (endedTimerRef.current) {
                clearTimeout(endedTimerRef.current);
                endedTimerRef.current = null;
            }

            const callId = createCallId();
            callRef.current = { callId, conversationId, role: "caller" };
            setCallState({
                ...IDLE_STATE,
                status: "outgoing",
                callId,
                conversationId,
                peer,
            });

            // Unlock the audio element while we still have the click gesture.
            playRemoteAudio();

            try {
                const { iceServers } = await requestIceConfig();
                if (callRef.current?.callId !== callId) return; // torn down meanwhile

                let stream;
                try {
                    stream = await getMicStream();
                } catch (err) {
                    console.warn("[SalesiveCall] getUserMedia failed", err);
                    teardown(micErrorReason(err));
                    return;
                }
                if (callRef.current?.callId !== callId) {
                    stream.getTracks().forEach((t) => t.stop());
                    return;
                }
                localStreamRef.current = stream;

                const pc = createPeerConnection(
                    iceServers,
                    callId,
                    conversationId,
                );
                stream.getTracks().forEach((track) => pc.addTrack(track, stream));

                // Backgrounding the app gets the microphone silenced by the OS — no error, no
                // state change, nothing for us to notice. Keep the page audible, and if we are
                // silenced anyway, at least find out.
                backgroundGuardRef.current = attachCallBackgroundGuard(stream, {
                    onMicSilenced: (silenced) =>
                        setCallState((prev) => ({ ...prev, micSilenced: silenced })),
                });

                const offer = await pc.createOffer();
                await pc.setLocalDescription(offer);
                if (callRef.current?.callId !== callId) return;

                emitCallOffer(
                    { callId, conversationId, sdp: offer, media: "audio" },
                    (response) => {
                        if (callRef.current?.callId !== callId) return;
                        if (!response || response.success === false) {
                            const reason =
                                response?.reason === "busy"
                                    ? "busy"
                                    : "unavailable";
                            teardown(reason);
                        }
                    },
                );

                // Give up ringing after a while — cancel and show "No answer".
                ringTimerRef.current = setTimeout(() => {
                    ringTimerRef.current = null;
                    if (callRef.current?.callId !== callId) return;
                    if (callRef.current.role !== "caller") return;
                    emitCallCancel({ callId, conversationId });
                    teardown("no-answer");
                }, RING_TIMEOUT_MS);
            } catch (err) {
                console.error("[SalesiveCall] startCall failed", err);
                if (callRef.current?.callId === callId) {
                    teardown("error");
                }
            }
        },
        [
            createPeerConnection,
            emitCallCancel,
            emitCallOffer,
            getMicStream,
            playRemoteAudio,
            requestIceConfig,
            teardown,
        ],
    );

    // ── Callee: accept the ringing call ───────────────────────────────
    const acceptCall = useCallback(async () => {
        const incoming = incomingOfferRef.current;
        const tracked = callRef.current;
        if (!incoming || !tracked || tracked.role !== "callee") return;
        const { callId, conversationId } = tracked;

        if (incomingTimerRef.current) {
            clearTimeout(incomingTimerRef.current);
            incomingTimerRef.current = null;
        }
        setCallState((prev) => ({ ...prev, status: "connecting" }));

        // Unlock the audio element while we still have the click gesture.
        playRemoteAudio();

        try {
            const { iceServers } = await requestIceConfig();
            if (callRef.current?.callId !== callId) return;

            let stream;
            try {
                stream = await getMicStream();
            } catch (err) {
                console.warn("[SalesiveCall] getUserMedia failed", err);
                // Let the caller know instead of leaving them ringing.
                emitCallReject({ callId, conversationId, reason: "mic-unavailable" });
                teardown(micErrorReason(err));
                return;
            }
            if (callRef.current?.callId !== callId) {
                stream.getTracks().forEach((t) => t.stop());
                return;
            }
            localStreamRef.current = stream;

            const pc = createPeerConnection(iceServers, callId, conversationId);
            stream.getTracks().forEach((track) => pc.addTrack(track, stream));

            // Backgrounding the app gets the microphone silenced by the OS — no error, no
            // state change, nothing for us to notice. Keep the page audible, and if we are
            // silenced anyway, at least find out.
            backgroundGuardRef.current = attachCallBackgroundGuard(stream, {
                onMicSilenced: (silenced) =>
                    setCallState((prev) => ({ ...prev, micSilenced: silenced })),
            });

            await pc.setRemoteDescription(incoming.sdp);
            flushPendingCandidates();

            const answer = await pc.createAnswer();
            await pc.setLocalDescription(answer);
            if (callRef.current?.callId !== callId) return;

            emitCallAnswer({ callId, sdp: answer }, (response) => {
                if (callRef.current?.callId !== callId) return;
                if (!response || response.success === false) {
                    // Lost the answer race / call gone — call:busy also covers this.
                    teardown("busy");
                }
            });
        } catch (err) {
            console.error("[SalesiveCall] acceptCall failed", err);
            if (callRef.current?.callId === callId) {
                teardown("error");
            }
        }
    }, [
        createPeerConnection,
        emitCallAnswer,
        emitCallReject,
        flushPendingCandidates,
        getMicStream,
        playRemoteAudio,
        requestIceConfig,
        teardown,
    ]);

    // ── Callee: decline the ringing call ──────────────────────────────
    const rejectCall = useCallback(() => {
        const tracked = callRef.current;
        if (!tracked || tracked.role !== "callee") return;
        emitCallReject({
            callId: tracked.callId,
            conversationId: tracked.conversationId,
        });
        teardown();
    }, [emitCallReject, teardown]);

    // ── Hang up (connected) / cancel (still ringing) ──────────────────
    const hangup = useCallback(() => {
        const tracked = callRef.current;
        if (!tracked) return;
        const { callId, conversationId, role } = tracked;
        if (role === "caller" && !connectedAtRef.current) {
            // Aborting our own ring (server also finalizes post-answer calls
            // on call:cancel, so a not-yet-ICE-connected call is covered too).
            emitCallCancel({ callId, conversationId });
        } else if (role === "callee" && !connectedAtRef.current) {
            // Cancelling mid-accept: if our answer hasn't reached the server
            // the reject dismisses the ring; if it has, the hangup ends the
            // call. Exactly one acts server-side (the other no-ops).
            emitCallReject({ callId, conversationId, reason: "cancelled" });
            emitCallHangup({ callId, conversationId });
        } else {
            emitCallHangup({ callId, conversationId });
        }
        teardown();
    }, [emitCallCancel, emitCallHangup, emitCallReject, teardown]);

    const toggleSpeaker = useCallback(() => {
        setCallState((prev) => ({ ...prev, speakerOn: !prev.speakerOn }));
    }, []);

    const toggleMute = useCallback(() => {
        const stream = localStreamRef.current;
        if (!stream) return;
        setCallState((prev) => {
            const muted = !prev.muted;
            stream.getAudioTracks().forEach((track) => {
                track.enabled = !muted;
            });
            return { ...prev, muted };
        });
    }, []);

    // ── Socket subscriptions (registered once; handlers use refs) ─────
    useEffect(() => {
        const cleanups = [
            onCallIncoming((payload) => {
                const callId = payload?.callId;
                if (!callId || !payload?.sdp) return;
                // De-dupe repeats and ignore new rings while already on a call.
                if (seenIncomingRef.current.has(callId)) return;
                seenIncomingRef.current.add(callId);
                if (seenIncomingRef.current.size > 50) {
                    const first = seenIncomingRef.current
                        .values()
                        .next().value;
                    seenIncomingRef.current.delete(first);
                }
                if (callRef.current) return;
                if (endedTimerRef.current) {
                    clearTimeout(endedTimerRef.current);
                    endedTimerRef.current = null;
                }

                incomingOfferRef.current = payload;
                pendingCandidatesRef.current = [];
                callRef.current = {
                    callId,
                    conversationId: payload.conversationId,
                    role: "callee",
                };
                setCallState({
                    ...IDLE_STATE,
                    status: "incoming",
                    callId,
                    conversationId: payload.conversationId,
                    peer: {
                        name:
                            payload.from?.name ||
                            payload.shop?.name ||
                            "Support",
                        avatar:
                            payload.from?.avatar ||
                            payload.shop?.logo ||
                            null,
                        shopName: payload.shop?.name || null,
                    },
                });

                // Safety: dismiss the ring if nothing terminal ever arrives.
                incomingTimerRef.current = setTimeout(() => {
                    incomingTimerRef.current = null;
                    if (
                        callRef.current?.callId === callId &&
                        callRef.current.role === "callee" &&
                        !pcRef.current
                    ) {
                        teardown();
                    }
                }, INCOMING_DISMISS_MS);
            }),

            onCallAnswered(async (payload) => {
                const tracked = callRef.current;
                if (!tracked || tracked.callId !== payload?.callId) return;
                if (tracked.role !== "caller") return;
                const pc = pcRef.current;
                if (!pc || !payload.sdp) return;
                if (ringTimerRef.current) {
                    clearTimeout(ringTimerRef.current);
                    ringTimerRef.current = null;
                }
                try {
                    await pc.setRemoteDescription(payload.sdp);
                    flushPendingCandidates();
                    setCallState((prev) => {
                        // The visitor dialed "the store"; a PERSON answered. Put
                        // their name and face on the call UI in place of the shop
                        // logo, so the visitor knows who they are talking to.
                        const peer = payload.answeredBy?.name
                            ? {
                                  ...prev.peer,
                                  name: payload.answeredBy.name,
                                  avatar:
                                      payload.answeredBy.avatar ||
                                      prev.peer?.avatar ||
                                      null,
                              }
                            : prev.peer;
                        return prev.status === "connected"
                            ? { ...prev, peer }
                            : { ...prev, status: "connecting", peer };
                    });
                } catch (err) {
                    console.error(
                        "[SalesiveCall] setRemoteDescription failed",
                        err,
                    );
                    emitCallHangup({
                        callId: tracked.callId,
                        conversationId: tracked.conversationId,
                    });
                    teardown("error");
                }
            }),

            onCallIceCandidate((payload) => {
                const tracked = callRef.current;
                if (!tracked || tracked.callId !== payload?.callId) return;
                if (!payload.candidate) return;
                const pc = pcRef.current;
                if (pc && pc.remoteDescription) {
                    pc.addIceCandidate(payload.candidate).catch((err) => {
                        console.warn(
                            "[SalesiveCall] addIceCandidate failed",
                            err,
                        );
                    });
                } else {
                    pendingCandidatesRef.current.push(payload.candidate);
                }
            }),

            onCallRejected((payload) => {
                if (callRef.current?.callId !== payload?.callId) return;
                teardown("declined");
            }),

            onCallCancelled((payload) => {
                if (callRef.current?.callId !== payload?.callId) return;
                // Callee-side ring dismissed (caller cancelled / answered elsewhere).
                teardown();
            }),

            onCallEnded((payload) => {
                if (callRef.current?.callId !== payload?.callId) return;
                teardown("ended");
            }),

            onCallBusy((payload) => {
                const tracked = callRef.current;
                if (!tracked || tracked.callId !== payload?.callId) return;
                // Caller: peer busy. Callee: our answer lost the race — dismiss.
                teardown(tracked.role === "caller" ? "busy" : null);
            }),

            onCallUnavailable((payload) => {
                if (callRef.current?.callId !== payload?.callId) return;
                teardown("unavailable");
            }),

            onCallError((payload) => {
                if (!callRef.current) return;
                if (payload?.callId && payload.callId !== callRef.current.callId)
                    return;
                console.warn("[SalesiveCall] call:error", payload);
                teardown(payload?.reason === "disabled" ? "disabled" : "error");
            }),
        ];

        return () => {
            cleanups.forEach((cleanup) => cleanup());
        };
    }, [
        onCallIncoming,
        onCallAnswered,
        onCallIceCandidate,
        onCallRejected,
        onCallCancelled,
        onCallEnded,
        onCallBusy,
        onCallUnavailable,
        onCallError,
        emitCallHangup,
        flushPendingCandidates,
        teardown,
    ]);

    // Full cleanup on unmount — also tell the peer we're gone.
    useEffect(() => {
        return () => {
            const tracked = callRef.current;
            if (tracked) {
                if (tracked.role === "caller" && !connectedAtRef.current) {
                    emitCallCancel({
                        callId: tracked.callId,
                        conversationId: tracked.conversationId,
                    });
                } else if (connectedAtRef.current) {
                    emitCallHangup({
                        callId: tracked.callId,
                        conversationId: tracked.conversationId,
                    });
                }
            }
            teardown();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (["connecting", "connected"].includes(callState.status)) {
            applySpeakerRoute(audioElRef.current, callState.speakerOn);
        }
    }, [callState.speakerOn, callState.status]);

    return {
        ...callState,
        startCall,
        acceptCall,
        rejectCall,
        hangup,
        toggleMute,
        toggleSpeaker,
        attachAudioElement,
    };
}

export default useCall;
